import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CustomerLinkProps {
  /** Customer row id. When absent the name renders as plain text. */
  customerId?: string | null;
  /** Display name. Falls back to a dash when empty. */
  name?: string | null;
  className?: string;
  /** Run before navigating, e.g. to close the sheet or dialog the link sits in. */
  onNavigate?: () => void;
  /**
   * Show a warning icon when there is no customer record to link to.
   * Only worth it on detail views; tables get plain text instead.
   */
  showUnlinkedWarning?: boolean;
  /** Rendered instead of the name when the name itself is empty. */
  emptyLabel?: string;
  /**
   * Open in a new tab. Use where navigating away would lose work, such as the
   * duplicate warning shown while someone is still filling in a new customer.
   */
  newTab?: boolean;
}

/**
 * The customer name, as a link to the customer card wherever we know which
 * customer it is. Real <Link> (not a button) so middle-click and
 * ctrl/cmd-click open the card in a new tab; clicks stop propagating so the
 * link still works inside clickable table rows and sheet headers.
 */
export function CustomerLink({
  customerId,
  name,
  className,
  onNavigate,
  showUnlinkedWarning = false,
  emptyLabel = '-',
  newTab = false,
}: CustomerLinkProps) {
  const label = name?.trim() || emptyLabel;

  if (!customerId) {
    // Plain span, so callers can keep using truncate/block utilities unchanged.
    if (!showUnlinkedWarning) return <span className={className}>{label}</span>;
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <span>{label}</span>
        <span title="No customer record linked">
          <AlertTriangle className="h-4 w-4 text-warning" />
        </span>
      </span>
    );
  }

  return (
    <Link
      to={`/customers/${customerId}`}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noreferrer' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onNavigate?.();
      }}
      title={newTab ? `Open ${label}'s customer card in a new tab` : `Open ${label}'s customer card`}
      className={cn(
        'inline-flex items-center gap-1 rounded-sm hover:text-primary hover:underline',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        className,
      )}
    >
      {label}
    </Link>
  );
}
